import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../contexts/AuthContext';
import dcqService from '../../services/dcqService';
import { UserService } from '../../services/userService';
import { 
  Send, 
  Loader, 
  Calendar, 
  Clock, 
  User,
  ChevronLeft, 
  FileText, 
  Edit, 
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Clipboard,
  Hash,
  GitBranch,
  Activity,
  Building,
  UserCheck,
  ArrowRight,
  TrendingUp,
  MoreVertical,
  Check
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const SingleDCQ = () => {
  const { user: currentUser } = useContext(AuthContext);
  const { dcqId } = useParams();
  const navigate = useNavigate();
  const [dcq, setDcq] = useState(null);
  const [comments, setComments] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [newComment, setNewComment] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isCommentFocused, setIsCommentFocused] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError('');

      if (!dcqId) {
        setError('No DCQ ID provided');
        return;
      }

      const [dcqData, commentsData] = await Promise.all([
        dcqService.getSingleDCQ(dcqId),
        dcqService.getCommentsForDCQ(dcqId)
      ]);

      if (!dcqData) {
        setError('DCQ not found');
        return;
      }

      setDcq(dcqData);
      setComments(commentsData || []);
      setSelectedStatus(dcqData.status);

      const userIds = [...new Set((commentsData || [])
        .map(comment => {
          const userId = comment.author?.id || comment.user;
          return typeof userId === 'number' ? userId : null;
        })
        .filter(Boolean)
      )];

      if (userIds.length) {
        const userProfileMap = {};
        await Promise.all(userIds.map(async (userId) => {
          try {
            const profile = await UserService.getUserProfile(userId);
            if (profile) {
              userProfileMap[userId] = profile;
            }
          } catch (error) {
            console.warn(`Failed to fetch profile for user ${userId}:`, error);
          }
        }));
        setUserProfiles(userProfileMap);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.status === 404 
        ? 'DCQ not found. It may have been deleted or you may not have permission to view it.'
        : `An error occurred while fetching the data: ${error.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dcqId]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || isSubmittingComment) return;
  
    if (!currentUser) {
      toast.error('Please log in to add comments');
      return;
    }
  
    setIsSubmittingComment(true);
    try {
      const response = await dcqService.addComment(dcqId, newComment.trim());
      
      if (response) {
        await fetchData();
        setNewComment('');
        toast.success('Comment added successfully');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      const now = new Date();
      const diffTime = now - date; // Remove Math.abs to get proper direction
      const diffMinutes = Math.floor(diffTime / (1000 * 60));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Handle future dates
      if (diffTime < 0) {
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Less than a minute
      if (diffMinutes < 1) {
        return 'Just now';
      }
      
      // Less than an hour
      if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
      }
      
      // Less than a day
      if (diffHours < 24) {
        return `${diffHours}h ago`;
      }
      
      // Less than a week
      if (diffDays < 7) {
        return `${diffDays}d ago`;
      }
      
      // More than a week - show full date
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', dateString);
      return 'Invalid date';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { hour: '2-digit', minute: '2-digit' };
      return new Date(dateString).toLocaleTimeString(undefined, options);
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid time';
    }
  };

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
  };

  const handleUpdateDCQ = async () => {
    setIsUpdatingStatus(true);
    try {
      await dcqService.updateDCQ(dcqId, { ...dcq, status: selectedStatus });
      toast.success('DCQ status updated successfully');
      await fetchData();
    } catch (error) {
      console.error('Error updating DCQ status:', error);
      toast.error('Failed to update DCQ status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const statusOptions = [
    'New',
    'Needs Clarification',
    'Waiting for Approval',
    'Verification',
    'Implementation',
    'Closed'
  ];

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700';
    const s = status.toLowerCase();
    if (s === 'new') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (s === 'needs clarification') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (s === 'waiting for approval') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (s === 'verification') return 'bg-purple-50 text-purple-700 border-purple-200';
    if (s === 'implementation') return 'bg-orange-50 text-orange-700 border-orange-200';
    if (s === 'closed') return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700';
  };

  const getStatusIcon = (status) => {
    if (!status) return <Activity className="w-4 h-4" />;
    const s = status.toLowerCase();
    if (s === 'new') return <CheckCircle className="w-4 h-4" />;
    if (s === 'needs clarification') return <AlertTriangle className="w-4 h-4" />;
    if (s === 'waiting for approval') return <Clock className="w-4 h-4" />;
    if (s === 'verification') return <CheckCircle className="w-4 h-4" />;
    if (s === 'implementation') return <TrendingUp className="w-4 h-4" />;
    if (s === 'closed') return <Check className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const renderComment = (comment, index) => {
    const userId = comment.author?.id || comment.user?.id || comment.user;
    const userProfile = userProfiles[userId];
  
    let userName = 'Unknown User';
    if (userProfile?.first_name && userProfile?.last_name) {
      userName = `${userProfile.first_name} ${userProfile.last_name}`;
    } else if (comment.author?.email) {
      userName = comment.author.email;
    } else if (comment.user?.email) {
      userName = comment.user.email;
    } else if (typeof userId === 'number') {
      userName = `User ${userId}`;
    }
    
    const createdDate = comment.created || comment.created_at;
  
    return (
      <motion.div
        key={comment.id || index}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        className="group"
      >
        <div className="flex space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-semibold shadow-sm dark:shadow-gray-900/20">
              {userName.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="flex-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 shadow-sm dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700 hover:shadow-md dark:hover:shadow-gray-900/20 transition-all duration-200">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{userName}</h4>
                <span className="text-xs text-gray-400 dark:text-gray-500">{formatDate(createdDate)}</span>
              </div>
              <p className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed">{comment.comment}</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md dark:shadow-gray-900/20 text-center">
          <Loader className="w-12 h-12 text-primary-600 animate-spin mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-gray-800 dark:text-gray-100">Loading DCQ data...</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400">Please wait while we fetch the DCQ details</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !dcq) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center items-center p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md dark:shadow-gray-900/20 max-w-md w-full">
          <div className="flex flex-col items-center">
            <div className="bg-red-100 p-3 rounded-full mb-4">
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 text-center">Unable to Load DCQ</h2>
            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">{error || 'No DCQ data available.'}</p>
            <button
              onClick={handleGoBack}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-300"
            >
              <ChevronLeft className="mr-2" size={18} />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={handleGoBack}
            className="group flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200 mb-6"
          >
            <ChevronLeft className="w-5 h-5 mr-1 group-hover:transform group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="font-medium">Back to DCQs</span>
          </button>

          {/* Main header card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 overflow-hidden">
            <div className="bg-primary-600 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-primary-500 rounded-lg">
                    <Hash className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-white">DCQ #{dcq.id}</h1>
                    <p className="text-primary-100 mt-1">Data Clarification Query</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`px-4 py-2 rounded-full flex items-center space-x-2 ${getStatusColor(dcq.status)} border`}>
                    {getStatusIcon(dcq.status)}
                    <span className="font-semibold text-sm">{dcq.status || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick stats */}
            <div className="px-8 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center text-gray-600 dark:text-gray-300">
                    <MessageSquare className="w-4 h-4 mr-1" />
                    <span>{comments.length} comments</span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-300">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>Created {formatDate(dcq.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left column - Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* DCQ Details Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 overflow-hidden"
            >
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                  <div className="p-2 bg-primary-100 rounded-lg mr-3">
                    <FileText className="w-5 h-5 text-primary-600" />
                  </div>
                  Query Details
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="group">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <GitBranch className="w-4 h-4 mr-1" />
                        Type
                      </div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.type || 'N/A'}</div>
                    </div>
                    
                    <div className="group">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <Activity className="w-4 h-4 mr-1" />
                        Action
                      </div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.action || 'N/A'}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="group">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <Building className="w-4 h-4 mr-1" />
                        Site
                      </div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.sitename || 'N/A'}</div>
                    </div>
                    
                    <div className="group">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <UserCheck className="w-4 h-4 mr-1" />
                        Participant
                      </div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.participant_display_id || dcq.participant || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Change details section */}
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <Edit className="w-4 h-4 mr-2 text-primary-600" />
                    Change Request
                  </h3>
                  
                  <div className="space-y-4">
                    {dcq.initial_value && dcq.new_value && (
                      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Initial Value</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.initial_value}</div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400 dark:text-gray-500 mx-4" />
                          <div className="flex-1">
                            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">New Value</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.new_value}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {dcq.requested_change && (
                      <div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Requested Change</div>
                        <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
                          <p className="text-gray-900 dark:text-gray-100">{dcq.requested_change}</p>
                        </div>
                      </div>
                    )}
                    
                    {dcq.reason_for_change && (
                      <div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Reason for Change</div>
                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                          <p className="text-gray-700 dark:text-gray-200">{dcq.reason_for_change}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Comments Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 overflow-hidden"
            >
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center justify-between">
                  <span className="flex items-center">
                    <div className="p-2 bg-primary-100 rounded-lg mr-3">
                      <MessageSquare className="w-5 h-5 text-primary-600" />
                    </div>
                    Discussion
                  </span>
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">{comments.length} comments</span>
                </h2>

                {/* Add comment box - always visible at top */}
                <div className={`mb-6 transition-all duration-300 ${isCommentFocused ? 'scale-[1.02]' : ''}`}>
                  <div className="flex space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gray-500 flex items-center justify-center text-white font-semibold">
                        {currentUser?.first_name?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    </div>
                    <div className="flex-1">
                      <textarea
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400
                          ${isCommentFocused 
                            ? 'border-primary-400 shadow-lg ring-4 ring-primary-100' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
                        rows={isCommentFocused ? "4" : "2"}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onFocus={() => setIsCommentFocused(true)}
                        onBlur={() => !newComment && setIsCommentFocused(false)}
                        placeholder="Share your thoughts..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            e.preventDefault();
                            handleAddComment();
                          }
                        }}
                      />
                      <AnimatePresence>
                        {(isCommentFocused || newComment) && (
                          <motion.div 
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex justify-between items-center mt-3"
                          >
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Press <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 font-mono text-xs">Ctrl+Enter</kbd> to submit
                            </div>
                            <button
                              className={`px-5 py-2 bg-primary-600 text-white rounded-lg
                                transition-all duration-200 flex items-center shadow-md dark:shadow-gray-900/20
                                ${(!newComment.trim() || isSubmittingComment) 
                                  ? 'opacity-50 cursor-not-allowed' 
                                  : 'hover:bg-primary-700 hover:shadow-lg dark:hover:shadow-gray-900/20'}`}
                              onClick={handleAddComment}
                              disabled={!newComment.trim() || isSubmittingComment}
                            >
                              {isSubmittingComment ? (
                                <>
                                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                                  Posting...
                                </>
                              ) : (
                                <>
                                  <Send size={16} className="mr-2" />
                                  Post
                                </>
                              )}
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>

                {/* Comments list */}
                <div className="space-y-4">
                  {comments.length > 0 ? (
                    <AnimatePresence>
                      {comments.map((comment, index) => renderComment(comment, index))}
                    </AnimatePresence>
                  ) : (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-12"
                    >
                      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                        <MessageSquare className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">No comments yet</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Be the first to start the discussion</p>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right column - Status and Actions */}
          <div className="space-y-6">
            {/* Status Update Card */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 p-6 sticky top-8"
            >
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <div className="p-2 bg-primary-100 rounded-lg mr-3">
                  <RefreshCw className="w-5 h-5 text-primary-600" />
                </div>
                Update Status
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-300 mb-2 block">Current Status</label>
                  <select
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 appearance-none cursor-pointer"
                    value={selectedStatus || dcq.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                
                <AnimatePresence>
                  {selectedStatus && selectedStatus !== dcq.status && (
                    <motion.button
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      onClick={handleUpdateDCQ}
                      disabled={isUpdatingStatus}
                      className="w-full px-5 py-3 bg-primary-600 text-white rounded-lg 
                        hover:bg-primary-700 transition-all duration-200 
                        flex items-center justify-center shadow-md dark:shadow-gray-900/20 hover:shadow-lg dark:hover:shadow-gray-900/20
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdatingStatus ? (
                        <>
                          <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-5 w-5" />
                          Update Status
                        </>
                      )}
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              {/* Activity timeline preview */}
              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Recent Activity</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-1.5"></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 dark:text-gray-300">Status changed to <span className="font-medium">{dcq.status}</span></p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(dcq.updated_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full mt-1.5"></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 dark:text-gray-300">DCQ created</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(dcq.created_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleDCQ;